import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { motion, stagger } from 'framer-motion';
import { PillTag } from '@/components/pill-tag';
import { FeatureCard } from '@/components/card/feature-card';
import { CampaignModesSpotlight } from '@/components/campaign-modes-spotlight';
export const Route = createFileRoute('/_layout/features/')({
    component: FeaturesPage,
    head: () => ({
        meta: [
            { title: 'Features — Campaign Analytics, QR Tracking & Smart Links | Adora' },
            {
                name: 'description',
                content: 'Explore Adora features: campaign management, Smart Links, QR analytics, A/B testing, geolocation tracking, and more—for offline and online marketing.',
            },
            { property: 'og:title', content: 'Features — Campaign Analytics, QR Tracking & Smart Links | Adora' },
            {
                property: 'og:description',
                content: 'Explore Adora features: campaign management, Smart Links, QR analytics, A/B testing, geolocation tracking, and more—for offline and online marketing.',
            },
        ],
    }),
});
const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};
function FeaturesPage() {
    const { t } = useTranslation('marketing');
    const featureCards = [
        { key: 'campaignManager', plans: ['free', 'solo', 'team'] },
        { key: 'smartLinks', plans: ['free', 'solo', 'team'] },
        { key: 'landingPages', plans: ['free', 'solo', 'team'] },
        { key: 'analyticsDashboard', plans: ['free', 'solo', 'team'] },
        { key: 'timelineFatigue', plans: ['free', 'solo', 'team'] },
        { key: 'unlimitedScans', plans: ['free', 'solo', 'team'] },
        { key: 'feedbackCampaigns', plans: ['solo', 'team'] },
        { key: 'showroom', plans: ['solo', 'team'] },
        { key: 'autoStore', plans: ['solo', 'team'] },
        { key: 'customThemes', plans: ['solo', 'team'] },
        { key: 'abTesting', plans: ['solo', 'team'] },
        { key: 'comparison', plans: ['solo', 'team'] },
        { key: 'advancedAnalytics', plans: ['solo', 'team'] },
        { key: 'geolocation', plans: ['team'] },
        { key: 'teamCollaboration', plans: ['team'] },
        { key: 'webMobile', plans: ['free', 'solo', 'team'] },
    ];
    return (<div className="min-h-screen bg-background text-foreground">
            <motion.section className="border-b bg-muted/30" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} transition={{ delayChildren: stagger(0.08) }}>
                <div className="mx-auto max-w-5xl px-4 py-14 md:py-20">
                    {/* Hero */}
                    <motion.div className="space-y-3 text-center" variants={fadeUp} transition={{ duration: 0.5, ease: 'easeOut' }}>
                        <PillTag label={t('features.hero.pill')}/>
                        <h1 className="text-3xl font-bold md:text-4xl">{t('features.hero.title')}</h1>
                        <p className="text-sm text-muted-foreground md:text-base">{t('features.hero.subtitle')}</p>
                    </motion.div>

                    {/* Feature grid */}
                    <div className="mt-10 grid gap-6 md:grid-cols-3">
                        {featureCards.map(({ key, plans }, index) => {
            const basePath = `features.cards.${key}`;
            const title = t(`${basePath}.title`);
            const body = t(`${basePath}.body`);
            return (<motion.div key={key} variants={fadeUp} transition={{ duration: 0.45, delay: 0.05 + index * 0.04 }} whileHover={{ y: -4, transition: { duration: 0.15 } }}>
                                    <FeatureCard title={title} body={body} plans={plans}/>
                                </motion.div>);
        })}
                    </div>

                    {/* Use cases summary */}
                    <motion.div className="mt-14 border-t pt-10" variants={fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
                        <h2 className="text-2xl font-semibold md:text-3xl">{t('features.useCases.title')}</h2>
                        <p className="mt-2 text-sm text-muted-foreground md:text-base">
                            {t('features.useCases.subtitle')}
                        </p>
                        <div className="mt-6 grid gap-4 md:grid-cols-2 text-xs text-muted-foreground">
                            {['restaurants', 'packaged', 'services', 'creators'].map((key, index) => (<motion.div key={key} className="rounded-2xl border bg-card p-4" variants={fadeUp} transition={{ duration: 0.45, delay: 0.15 + index * 0.04 }} whileHover={{ y: -3, transition: { duration: 0.15 } }}>
                                    <h3 className="text-sm font-semibold mb-1">
                                        {t(`features.useCases.${key}.title`)}
                                    </h3>
                                    <p>{t(`features.useCases.${key}.body`)}</p>
                                </motion.div>))}
                        </div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Campaign modes spotlight — same component used on /, /pricing
            so the showroom + auto-store + custom-theme pitch is identical
            wherever a visitor lands first. */}
            <CampaignModesSpotlight eyebrow={t('features.examples.pill', { defaultValue: 'Campaign modes' })} title={t('features.examples.title', {
            defaultValue: 'See what your campaign page actually looks like',
        })} subtitle={t('features.examples.subtitle', {
            defaultValue: '35 themes × 42 animated backgrounds. Every campaign mode picks up your brand palette + motion. These are real, live previews — not mockups.',
        })}/>
        </div>);
}
